module Client.Page.User.MyProfile

open System
open Client.Infrastructure.Api
open Client.Components
open Client.Domain
open Client.DomainTypes
open Client.InfrastructureTypes
open Elmish
open Fable.React.Props
open Fulma
open Shared
open Shared.Dto.Page.UserList
open Shared.Dto.User
open Fable.React
open Client.Forms
open Client.DomainTypes.Msg
open Shared.Dto.Dto
open Shared.Infrastructure


type DataModel = {
    Session: UserSession
    Packages: IdValue<Package> list
    Profile: FullUserDto
    ChangePasswordModel: ChangePassword.Model
    OpenTab: MyProfileTabs.Tab
}

type LoadingModel = {
    Session: UserSession
    Profile: FullUserDto option
    Packages: IdValue<Package> list option
}

type Model = Loadable<DataModel, LoadingModel>

let tryCreateDataModel (loading: LoadingModel) : DataModel option =
    Option.map2
        (fun packages profile -> {
            Session = loading.Session
            Packages = packages
            Profile = profile
            ChangePasswordModel = ChangePassword.init loading.Session
            OpenTab = MyProfileTabs.Profile
        })
        loading.Packages
        loading.Profile

let getModel (loading: LoadingModel) : Model =
    tryCreateDataModel loading
    |> Option.map Loadable.Data
    |> Option.defaultValue (Loadable.Loading loading)


let private profileBox (model: DataModel) =
    let heading = Heading.p [] [ str "Benutzerdaten" ]

    let form =
        form [] [
            Field.div [ Field.IsHorizontal ] [
                Field.body [] [
                    Field.div [] [
                        Label.label [] [ str "Vorname" ]

                        Input.text [
                            Input.IsReadOnly true
                            Input.IsStatic true
                            Input.Value(getFirstName model.Profile)
                        ]
                    ]

                    Field.div [] [
                        Label.label [] [ str "Nachname" ]

                        Input.text [
                            Input.IsReadOnly true
                            Input.IsStatic true
                            Input.Value(getLastName model.Profile)
                        ]
                    ]
                ]
            ]

            Field.div [] [
                Label.label [] [ str "E-Mail Adresse" ]
                Input.text [
                    Input.IsReadOnly true
                    Input.IsStatic true
                    Input.Value(getMail model.Profile)
                ]
            ]

            Field.div [ Field.IsHorizontal ] [
                Field.body [] [
                    Field.div [] [
                        Label.label [] [ str "Benutzer-Typ" ]
                        Input.text [
                            Input.IsReadOnly true
                            Input.IsStatic true
                            Input.Value(userTypeToString model.Profile)
                        ]
                    ]

                    match model.Profile with
                    | FullUserDto.Admin _
                    | FullUserDto.RegularUser _ -> ()
                    | FullUserDto.TestUser testUser ->
                        Field.div [] [
                            Label.label [] [ str "Benutzer gültig bis" ]
                            Input.text [
                                Input.IsReadOnly true
                                Input.IsStatic true
                                Input.Value(testUser.ValidUntil |> DateTime.toShortString)
                            ]
                        ]
                ]
            ]
        ]

    Box.wrapWithHeading heading [ form ]

let private passwordBox (model: DataModel) dispatch =
    let headingContent = str "Passwort ändern"

    let boxContent =
        ChangePassword.view model.ChangePasswordModel (MyProfileMsg.ChangePasswordForm >> dispatch)

    Box.wrapWithHeading headingContent [ boxContent ]

let private getUserPackages (user: FullUserDto) =
    match user with
    | FullUserDto.Admin _
    | FullUserDto.TestUser _ -> None
    | FullUserDto.RegularUser user -> Some user.Packages

let private userHasBookedPackages (user: FullUserDto) =
    match getUserPackages user with
    | Some _ -> true
    | None -> false

let toPrice (amount: Decimal) : string = amount |> string |> sprintf "%s €"

let bookedPackagesTable (user: RegularUserDataDto) (model: DataModel) =
    let packages =
        user.Packages
        |> List.map (fun package ->
            model.Packages
            |> List.tryFind (fun p -> p.Id = package.Value.PackageId)
            |> Option.get
        )

    let totalAmount = packages |> List.sumBy (fun p -> p.Value.Price)

    let rows =
        packages
        |> List.sortBy (fun p -> p.Value.Name)
        |> List.map (fun package ->
            tr [] [
                td [] [ str package.Value.Name ]
                td [] [ package.Value.Price |> toPrice |> str ]
            ]
        )

    Table.table [
        Table.IsBordered
        Table.IsFullWidth
        Table.IsStriped
    ] [
        thead [] [
            tr [] [
                th [] [ str "Paket" ]
                th [] [ str "Preis/Jahr" ]
            ]
        ]
        tbody [] [
            yield! rows

            tr [] [
                th [] [ str "Gesamtpreis" ]
                th [] [ totalAmount |> toPrice |> str ]
            ]
        ]
    ]

let bookedPackagesBox (model: DataModel) =
    match model.Profile with
    | FullUserDto.Admin _ -> None
    | FullUserDto.TestUser _ -> None
    | FullUserDto.RegularUser user ->
        let content = bookedPackagesTable user model

        let headingContent = str "Gebuchte Pakete"

        [ Content.content [] [ content ] ] |> Box.wrapWithHeading headingContent |> Some

let private createTab dispatch (label: string) (tab: MyProfileTabs.Tab) (openTab: MyProfileTabs.Tab) =
    let onClick = (fun _ -> dispatch (MyProfileMsg.ChangeTab tab))

    Tabs.tab [ Tabs.Tab.IsActive(tab = openTab) ] [ a [ DOMAttr.OnClick onClick ] [ str label ] ]

let private createTabView dispatch (model: DataModel) =
    match model.OpenTab with
    | MyProfileTabs.Profile -> profileBox model
    | MyProfileTabs.BoughtPackages ->
        bookedPackagesBox model
        |> Option.defaultValue (div [] [ str "Das darf nicht passieren" ])
    | MyProfileTabs.ChangePassword -> passwordBox model dispatch
    | MyProfileTabs.NotificationSettings ->
        Box.wrapWithHeading (str "Benachrichtigungs-Einstellungen") [ NotificationSettings.view ]

let dataView dispatch (model: DataModel) =
    let tabs =
        Tabs.tabs [ Tabs.Size IsMedium ] [
            createTab dispatch "Profil" MyProfileTabs.Profile model.OpenTab

            if userHasBookedPackages model.Profile then
                createTab dispatch "Gebuchte Pakete" MyProfileTabs.BoughtPackages model.OpenTab
            else
                ()

            createTab dispatch "Passwort ändern" MyProfileTabs.ChangePassword model.OpenTab
        ]

    Container.container [ Container.isFullWidth ] [
        Heading.h1 [] [ str "Einstellungen" ]

        tabs

        createTabView dispatch model
    ]

let init (session: UserSession) =
    let requestData: AuthenticatedRequest<unit> = {
        SessionKey = session.SessionKey
        Data = ()
    }

    let loadingModel = {
        Session = session
        Packages = None
        Profile = None
    }

    let cmd =
        Cmd.batch [
            Cmd.OfAsync.perform api.getMyProfile requestData (MyProfileMsg.ProfileLoaded >> MyProfile)
            Cmd.OfAsync.perform api.getAllPackages requestData (MyProfileMsg.PackagesLoaded >> MyProfile)
        ]

    Loadable.Loading loadingModel, cmd

let map (modelMap: 'model -> Model) (cmdMap: 'msg -> Msg) (subModel: 'model, subCmd: Cmd<'msg>) =
    modelMap subModel, Cmd.map cmdMap subCmd

let update (msg: MyProfileMsg) (model: Model) =
    match msg, model with
    | MyProfileMsg.ChangePasswordForm formMsg, Loadable.Data data ->
        ChangePassword.update formMsg data.ChangePasswordModel
        |> (fun (formModel, cmd) ->
            Loadable.Data { data with ChangePasswordModel = formModel }, Cmd.map (ChangePasswordForm >> MyProfile) cmd
        )
    | MyProfileMsg.ProfileLoaded response, Loading loading ->
        match response with
        | Result.Ok profile ->
            let newLoadingModel = { loading with Profile = Some profile }

            getModel newLoadingModel, Cmd.none
        | Result.Error _ -> Loadable.Error "Ein Fehler ist aufgetreten beim Laden der Daten", Cmd.none
    | MyProfileMsg.PackagesLoaded response, Loading loading ->
        match response with
        | Result.Ok packages ->
            let newLoadingModel = { loading with Packages = Some packages }

            getModel newLoadingModel, Cmd.none
        | Result.Error _ -> Loadable.Error "Ein Fehler ist aufgetreten beim Laden der Daten", Cmd.none
    | MyProfileMsg.ChangeTab tab, Loadable.Data data -> Loadable.Data { data with OpenTab = tab }, Cmd.none
    | _, _ -> model, Cmd.none

let view dispatch (model: Model) = Loadable.view (dataView dispatch) model